import { Injectable } from '@angular/core';
import { FormattingService } from '../services/formatting.service';
import * as _ from 'underscore';
import { TranslateService } from '@ngx-translate/core';
import { DISABLED_OPTION_PROP, FormlyType, IExtraFieldConfigurationObject, IExtraTemplateOptions } from './ga-formly.service';
import { AbstractControl } from '@angular/forms';
import { GaEventService } from '../services/ga-event.service';
import { NgSelectComponent } from '@ng-select/ng-select';
import { distinctUntilChanged, pairwise, startWith } from 'rxjs/operators';
import { ExtraFieldType } from './extra-field-type';
import { util } from '../util';
import { OptionData } from 'select2';

export const EMAIL_ADDRESS_REG_EXP:RegExp = /^(?=.{1,254}$)(?=.{1,64}@)[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+(\.[-!#$%&'*+/0-9=?A-Z^_`a-z{|}~]+)*@[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$/;
export const ATTR_REAL_VALUE:string = 'data-real-value';
export const MIN_SEARCH_LENGTH:number = 2;

export const EVENT_FORMLY_VALUE_INIT:string = 'formly.defaultValueInit';
export const DEFAULT_KEY_PROP: string = 'id';

export interface IFormlySecureFields<T> {
  roFields?:{ [P in keyof T]:IExtraFieldConfigurationObject }; 	// read-only fields
  wFields?:{ [P in keyof T]:IExtraFieldConfigurationObject }; 	// writable fields
  sFields?:{ [P in keyof T]:IExtraFieldConfigurationObject }; 	// secure fields: read-only or writable based on user permission
}

export enum Select2Event {
  SELECT = 'select2:select',
  UNSELECT = 'select2:unselect',
  OPEN = 'select2:open',
  CLOSING = 'select2:closing'
}

@Injectable()
export class GaFormlyUtil {

  constructor(
    private formattingService:FormattingService,
    private translateService:TranslateService,
    private gaEventService:GaEventService
  ) {
    /**/
  }

  public createClassNames(type:string, key:string, label:string):string {
    label = util.processLabel(label);
    // separate by - when there is uppercase
    type = type.replace(/(?=[A-Z])/, '-').toLowerCase();

    return this.formattingService.join(
      label? `${type}-label-${label}` : undefined,
      label? `ff-label-${label}` : undefined,
      key? `${type}-key-${this.formattingService.snakeCase(key)}` : undefined,
      key? `ff-key-${this.formattingService.snakeCase(key)}` : undefined,
      ' '
    );
  }

  public createFieldArrayClassName(key:string):string {
    if (key) {
      key = key.toLowerCase().replace(/\s/g, '-').replace(/[^\w\-]/g, '_');
    }
    return key;
  }

  public getFormatFn(format:Function | string):Function {
    if (_.isFunction(format)) {
      return format;
    } else if (_.isString(format)) {
      return value => value? value[ format ] : undefined;
    }

    return _.identity;
  }

  public translateOptions(options:any[]):any[] {
    if (_.isObject(options[ 0 ])) {
      // translate every value from object ignoring the key 'id'
      _.each(options, (option:any, i:number) => {
        _.each(option, (v:string, k:string) => {
          if (k !== 'id' && _.isString(v)) {
            options[ i ][ k ] = this.translateService.instant(v);
          }
        });
      });
    } else {
      options = _.map(options, (option:string) => this.translateService.instant(option));
    }

    return options;
  }

  public onOptionsAndModelChange(selectElement:JQuery<HTMLSelectElement>, to:IExtraTemplateOptions, fc:AbstractControl):void {
    // if there is no options but there is model value then add to initial option list
    if ((!to.options || !to.options.length) && fc.value) {
      to.options = [ fc.value ];
    } else if (to.addInitialOption !== false && to.options?.length && to.initialValue && fc.value) {
      if (this.valueNotInOptions(to)) {
        this.addDisableInitialOption(selectElement, to, fc);
      } else {
        if (selectElement instanceof NgSelectComponent) {
          for (const item of to.options) {
            if (to.compareWith(item, fc.value)) {
              // options will be transformed to NgOption interface then we can fully set value available in the list
              // add timeout here until ng-select options has been fully transformed
              setTimeout(() => {
                fc.setValue(item);
              });
              break;
            }
          }
        }
      }
    }
    // this is for select element when it doesn't have initial value but value set after certain actions along the way
    // we should add options on it, this is already the case before
    // See reference at webshop.int.spec.js in integration test -> Line 118
    else if (to.options?.length && !to.initialValue && fc.value) {
      // check if programmatically set value is within the options
      if (this.valueNotInOptions(to, fc.value)) {
        // add programmatically set option value to select options
        to.options.unshift({
          ...fc.value,
          disabled: true
        });
      }
    }
  }

  public valueNotInOptions(to:IExtraTemplateOptions, providedValue?:any):boolean {
    let valueNotInOptions:boolean;
    const value:any = providedValue || to.initialValue;
    const valueProp:string = _.isString(to.valueProp)? to.valueProp : undefined;

    // check if given value exist within options based on trackBy or given property
    if (_.isObject(value) || valueProp) {
      let whereProp:{ [ key:string ]:number };
      if (valueProp) {
        whereProp = { [ valueProp ]: value };
      } else if (to.trackBy) {
        whereProp = { [ to.trackBy ]: value[ to.trackBy ] };
      } else {
        whereProp = { [DEFAULT_KEY_PROP]: value[DEFAULT_KEY_PROP] };
      }

      // ensure that the valueProp is also set, otherwise addDisableInitialOption is called indefinitely
      const wherePropValue:any = Object.values(whereProp)[0];
      valueNotInOptions = wherePropValue && !(_.where(to.options, whereProp).length > 0);
    } else {
      valueNotInOptions = !_.contains(to.options, value);
    }

    return valueNotInOptions;
  }

  private addDisableInitialOption(selectElement:JQuery<HTMLSelectElement>, to:IExtraTemplateOptions, fc:AbstractControl, providedValue?: any):void {
    const value:any = providedValue || _.clone(to.initialValue);

    // we should add guard here to prevent if initial value is different data type with options from server
    // e.g. initialValue = 4 and options [{ ... }]. See reference at userprofile_subcompany.int.spec.js -> 1st test case
    // It will cause exception because it tries to do Object.defineProperty() and break, so we let model value set to null by angular (default behavior)
    if (typeof value === typeof to.options[ 0 ]) {
      if (_.isObject(to.options[0]) && !selectElement.data('select2')) {
        // set disabled key here based on disabledProp
        value[<string>to.disabledProp] = true;
      }

      // add initial option to select options
      to.options.unshift(value);

      // we set model back to initial value because model value has been set to undefined by angular select
      // this is a workaround to prevent model from being changed when new options are added while the user has not changed anything on the form
      fc.patchValue(value);

      // we need to add timeout here to let the new options be processed in the DOM
      // only for select2
      setTimeout(() => {
        // set the initial value option to disabled because it doesn't exist in the list of options
        // this makes sure that the option cannot be selected by the user
        // data('select2') will make sure that select2 initiated and select2('data') make sure there are options available
        if (selectElement.data('select2') && selectElement.select2('data')?.[ 0 ]) {
          selectElement.select2('data')[ 0 ].disabled = true;
        } else {
          // only for value when type is not object
          if (!_.isObject(value)) {
            selectElement.find(`option[ng-reflect-ng-value="${value}"]`).prop('disabled', 'disabled');
          }
        }
      });
    }
  }

  public getSecuredFields(roFields:IExtraFieldConfigurationObject[], wFields:IExtraFieldConfigurationObject[], writePermission?:boolean):IFormlySecureFields<any> {
    const fields:IFormlySecureFields<any> = {
      sFields: {},
      roFields: {},
      wFields: {}
    };

    _.each(roFields, (field:IExtraFieldConfigurationObject) => {
      if (field.key) {
        fields.roFields[ `${field.key}` ] = field;
      }
    });

    _.each(wFields, (field:IExtraFieldConfigurationObject) => {
      if (field.key) {
        fields.wFields[ `${field.key}` ] = field;
      }
    });

    _.each(fields.wFields, (writableField:IExtraFieldConfigurationObject) => {
      fields.sFields[ `${writableField.key}` ] = writePermission? writableField : fields.roFields[ `${writableField.key}` ] || {};
    });

    return fields;
  }

  public defaultValueToUndefined(control:AbstractControl, field:IExtraFieldConfigurationObject):void {
    // don't register custom field in model
    if (field.type === FormlyType.CUSTOM) {
      return;
    }

    // should check this when formControl still pristine (first time load)
    if (control.pristine && control.value === null) {
      if (field.type !== FormlyType.CHECKBOX) {
        control.setValue(undefined, {
          onlySelf: true,
          emitEvent: false
        });
        control.markAsPristine();
        control.markAsUntouched();
      } else {
        // if field type is checkbox then we set default value to false rather than undefined
        control.setValue(false, {
          onlySelf: true,
          emitEvent: false
        });
      }

      this.gaEventService.sendMessage(EVENT_FORMLY_VALUE_INIT, {
        key: field.key,
        value: control.value
      });
    }
  }

  public initSelectField(to: IExtraTemplateOptions, formControl: AbstractControl): void {
    to.initialValue = formControl.value;
    to.valueProp = to.valueProp || _.identity;
    to.labelProp = to.labelProp || this.getFormatFn(to.format);
    // this object key will be used to disabled option
    to.disabledProp = to.disabledProp || DISABLED_OPTION_PROP;
  }

  public getSelectListener(to: IExtraTemplateOptions, formControl: AbstractControl, jSelect: JQuery<HTMLSelectElement>): void {
    formControl.valueChanges.pipe(
      distinctUntilChanged()
    ).subscribe(() => {
      // handle option behavior on option or model change
      this.onOptionsAndModelChange(jSelect, to, formControl);
    });

    to.optionsObserver
      .pipe(
        distinctUntilChanged()
      )
      .subscribe((options:any[]) => {
        // handle option behavior on option or model change
        this.onOptionsAndModelChange(jSelect, to, formControl);

        if (to.translateOptions) {
          to.options = this.translateOptions(options);
        }
      });
  }

  public getSelect2Listener(formControl:AbstractControl, jSelect:JQuery<HTMLSelectElement>, isMultiple:boolean):void {
    // for select2 every changed value we need to trigger change, so select2 element will be refreshed
    // we need to add timeout here to let angular digest element change
    formControl.valueChanges
      .pipe(
        startWith<any, any>(formControl.value),
        distinctUntilChanged(_.isEqual),
        pairwise()
      )
      .subscribe(() => {
        setTimeout(() => {
          if (util.isNullOrUndefinedOrEmpty(formControl.value)) {
            jSelect.val(null).trigger('change');
          } else {
            jSelect.trigger('change');
          }
        });
      });

    // listen to change on regular select and mark as dirty
    // so, formControl will re-validate the field
    jSelect.on('change', () => {
      formControl.markAsDirty();
      formControl.markAsTouched();
    });

    // watch select2 values -> set in model values
    jSelect.on(`${Select2Event.SELECT} ${Select2Event.UNSELECT}`, (event:JQueryEventObject) => {
      // put the new values in the model
      formControl.patchValue(this.getSelect2Values(jSelect, isMultiple));

      // clear formControl value when user click (x) icon, this is only for aSelect2 not for aMultiSelect
      if (event.type === Select2Event.UNSELECT && !isMultiple) {
        formControl.patchValue(undefined);
      }
    });
  }

  public getSelect2Values(jSelect:JQuery<HTMLSelectElement>, isMultiple:boolean):any[] {
    const select2Values:OptionData[] = jSelect.select2('data');
    const data:any[] = _.map(select2Values, (optionData:OptionData) => {
      return util.fromJson(optionData.element.getAttribute(ATTR_REAL_VALUE));
    });

    return data?.length? (isMultiple? data : data[0]) : undefined;
  }
}

export function testEmailAddressFn(value:string):boolean {
  return EMAIL_ADDRESS_REG_EXP.test(value);
}

export function trackByFunction(index:number, item:any):any {
  if (this.to?.format) {
    if (this.to.noTrack) {
      return item.value;
    } else if (this.to.trackBy) {
      return item.value[ this.to.trackBy ];
    } else {
      return item.value[ DEFAULT_KEY_PROP ];
    }
  } else {
    return item.value || item[DEFAULT_KEY_PROP] || index;
  }
}

export function compareFn(o1:any, o2:any): boolean {
  if (_.isObject(o1) && _.isObject(o2)) {
    if (_.isString(this.to?.valueProp)) {
      return o1[ this.to.valueProp ] === o2[ this.to.valueProp ];
    } else if (this.to?.trackBy) {
      return o1[ this.to.trackBy ] === o2[ this.to.trackBy ];
    } else {
      return o1[DEFAULT_KEY_PROP] === o2[DEFAULT_KEY_PROP];
    }
  } else {
    return o1 === o2;
  }
}

// this is the to change showError validation globally
// defined in SharedModule.imports.FormlyModule.forRoot({ extras: showError })
// workaround to only show validation in dirty state or when validation show is true
// https://github.com/ngx-formly/ngx-formly/issues/1180#issuecomment-423154658
export function showErrorValidation(field: ExtraFieldType): boolean {
  return field.formControl && field.formControl.invalid && (
    field.formControl.dirty || (field.options.parentForm && field.options.parentForm.submitted) || !!(field.field?.validation && field.field?.validation.show)
  );
}